import {
  LoginAttemptDeviceKind,
  LoginAttemptDeviceKindLabels,
  LoginAttemptMethod,
  LoginAttemptMethodLabels,
  LoginAttemptResult,
  LoginAttemptResultLabels,
  LoginAttemptStage,
  LoginAttemptStageLabels,
  LoginAttemptSystemBlock,
  LoginAttemptSystemBlockLabels,
  type LoginAttemptRecord
} from '@navio/nsdk';
import { URLKeys, type OrderingKeyOfUrlKey } from 'noddi-async';
import { NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import {
  copyToClipboardWithToast,
  NoddiBadge,
  type BadgeVariant,
  type NoddiColumnDef,
  type TableAction
} from 'noddi-ui-web';
import { entriesToOptions, formatPhoneNumber, getLabelMapFromEntries } from 'noddi-util';

type LoginAttemptOrderingKey = OrderingKeyOfUrlKey<typeof URLKeys.getPaginatedLoginAttempts>;

const METHOD_LABEL = getLabelMapFromEntries(LoginAttemptMethod, LoginAttemptMethodLabels);
const RESULT_LABEL = getLabelMapFromEntries(LoginAttemptResult, LoginAttemptResultLabels);
const STAGE_LABEL = getLabelMapFromEntries(LoginAttemptStage, LoginAttemptStageLabels);
const SYSTEM_BLOCK_LABEL = getLabelMapFromEntries(LoginAttemptSystemBlock, LoginAttemptSystemBlockLabels);
const DEVICE_KIND_LABEL = getLabelMapFromEntries(LoginAttemptDeviceKind, LoginAttemptDeviceKindLabels);

export const HELD_BY_US: readonly LoginAttemptSystemBlock[] = [
  LoginAttemptSystemBlock.BLOCKLIST,
  LoginAttemptSystemBlock.RATE_LIMIT,
  LoginAttemptSystemBlock.COUNTRY
];

export const SPAM_BLOCKS: readonly LoginAttemptSystemBlock[] = [
  LoginAttemptSystemBlock.CAPTCHA,
  LoginAttemptSystemBlock.SMS_PUMPING,
  LoginAttemptSystemBlock.TWILIO_FRAUD
];

export const FILTERABLE_BLOCKS: readonly LoginAttemptSystemBlock[] = [...HELD_BY_US, ...SPAM_BLOCKS];

const METHOD_ICON: Record<LoginAttemptMethod, NoddiIconName> = {
  [LoginAttemptMethod.EMAIL]: 'Mail',
  [LoginAttemptMethod.PHONE]: 'Phone',
  [LoginAttemptMethod.MAGIC_LINK]: 'Link',
  [LoginAttemptMethod.PASSKEY]: 'Lock',
  [LoginAttemptMethod.TOTP]: 'QrCode',
  [LoginAttemptMethod.RECOVERY_CODE]: 'Hash',
  [LoginAttemptMethod.PASSWORD_RESET]: 'LockOpen'
};

const RESULT_VARIANT: Record<LoginAttemptResult, BadgeVariant> = {
  [LoginAttemptResult.SUCCESS]: 'success',
  [LoginAttemptResult.INVALID_CREDENTIALS]: 'destructive',
  [LoginAttemptResult.USER_NOT_FOUND]: 'destructive',
  [LoginAttemptResult.INVALID_CODE]: 'destructive',
  [LoginAttemptResult.BLOCKED]: 'destructive',
  [LoginAttemptResult.INACTIVE]: 'destructive',
  [LoginAttemptResult.ERROR]: 'destructive',
  [LoginAttemptResult.MFA_CHALLENGE]: 'warning',
  [LoginAttemptResult.ACCOUNT_SELECTION]: 'warning',
  [LoginAttemptResult.SIGNUP_REQUIRED]: 'warning',
  [LoginAttemptResult.SMS_SENT]: 'warning',
  [LoginAttemptResult.CODE_ALREADY_SENT]: 'info'
};

const STAGE_VARIANT: Record<LoginAttemptStage, BadgeVariant> = {
  [LoginAttemptStage.STARTED]: 'info',
  [LoginAttemptStage.SMS_SENT]: 'warning',
  [LoginAttemptStage.COMPLETED]: 'outline'
};

const SYSTEM_BLOCK_VARIANT: Record<LoginAttemptSystemBlock, BadgeVariant> = {
  [LoginAttemptSystemBlock.NONE]: 'outline',
  [LoginAttemptSystemBlock.BLOCKLIST]: 'warning',
  [LoginAttemptSystemBlock.RATE_LIMIT]: 'warning',
  [LoginAttemptSystemBlock.COUNTRY]: 'warning',
  [LoginAttemptSystemBlock.CAPTCHA]: 'destructive',
  [LoginAttemptSystemBlock.SMS_PUMPING]: 'destructive',
  [LoginAttemptSystemBlock.TWILIO_FRAUD]: 'destructive'
};

const SYSTEM_BLOCK_ICON: Record<LoginAttemptSystemBlock, NoddiIconName> = {
  [LoginAttemptSystemBlock.NONE]: 'Info',
  [LoginAttemptSystemBlock.BLOCKLIST]: 'Lock',
  [LoginAttemptSystemBlock.RATE_LIMIT]: 'Clock',
  [LoginAttemptSystemBlock.COUNTRY]: 'Globe',
  [LoginAttemptSystemBlock.CAPTCHA]: 'Shield',
  [LoginAttemptSystemBlock.SMS_PUMPING]: 'MessageSquare',
  [LoginAttemptSystemBlock.TWILIO_FRAUD]: 'TriangleAlert'
};

export const methodOptions = entriesToOptions(LoginAttemptMethod, LoginAttemptMethodLabels).map((option) => ({
  ...option,
  icon: METHOD_ICON[option.value]
}));

export const resultOptions = entriesToOptions(LoginAttemptResult, LoginAttemptResultLabels);
export const stageOptions = entriesToOptions(LoginAttemptStage, LoginAttemptStageLabels);
export const deviceKindOptions = entriesToOptions(LoginAttemptDeviceKind, LoginAttemptDeviceKindLabels);

export function systemBlockOptions(values: readonly LoginAttemptSystemBlock[]) {
  return values.map((value) => ({
    value,
    label: SYSTEM_BLOCK_LABEL[value],
    icon: SYSTEM_BLOCK_ICON[value]
  }));
}

export function inBlockGroup(group: readonly LoginAttemptSystemBlock[], value: LoginAttemptSystemBlock): boolean {
  return group.some((member) => member === value);
}

function attemptIdentifier(attempt: LoginAttemptRecord): string | null {
  if (attempt.email) {
    return attempt.email;
  }
  if (attempt.phoneNumber) {
    return formatPhoneNumber(attempt.phoneNumber);
  }
  return null;
}

type LoginAttemptColumn = NoddiColumnDef<LoginAttemptRecord, unknown, LoginAttemptOrderingKey>;

const whenColumn: LoginAttemptColumn = {
  header: 'When',
  accessorKey: 'createdAt',
  meta: { type: 'date-time', orderingKey: 'created_at' }
};

const memberColumn: LoginAttemptColumn = {
  header: 'Member',
  accessorKey: 'userName',
  cell: ({ row }) => row.original.userName.trim() || null,
  meta: { orderingKey: 'user_name' }
};

const attemptColumns: LoginAttemptColumn[] = [
  {
    header: 'Method',
    accessorKey: 'method',
    cell: ({ row }) => (
      <NoddiBadge variant='outline'>
        <NoddiIcon name={METHOD_ICON[row.original.method]} size='xSmall' />
        {METHOD_LABEL[row.original.method]}
      </NoddiBadge>
    ),
    meta: { orderingKey: 'method' }
  },
  {
    header: 'Step',
    accessorKey: 'stage',
    cell: ({ row }) => (
      <NoddiBadge variant={STAGE_VARIANT[row.original.stage]}>{STAGE_LABEL[row.original.stage]}</NoddiBadge>
    ),
    meta: { orderingKey: 'stage' }
  },
  {
    header: 'Result',
    accessorKey: 'result',
    cell: ({ row }) => (
      <NoddiBadge variant={RESULT_VARIANT[row.original.result]}>{RESULT_LABEL[row.original.result]}</NoddiBadge>
    ),
    meta: { orderingKey: 'result' }
  },
  {
    header: 'Note',
    accessorKey: 'systemBlock',
    cell: ({ row }) => {
      const block = row.original.systemBlock;
      if (block === LoginAttemptSystemBlock.NONE) {
        return null;
      }
      return (
        <NoddiBadge variant={SYSTEM_BLOCK_VARIANT[block]}>
          <NoddiIcon name={SYSTEM_BLOCK_ICON[block]} size='xSmall' />
          {SYSTEM_BLOCK_LABEL[block]}
        </NoddiBadge>
      );
    },
    meta: { orderingKey: 'system_block' }
  },
  {
    header: 'Identifier',
    accessorFn: (row) => attemptIdentifier(row) ?? '',
    cell: ({ row }) => attemptIdentifier(row.original),
    meta: { orderingKey: 'identifier' }
  },
  {
    header: 'IP address',
    accessorKey: 'ipAddress',
    cell: ({ row }) => row.original.ipAddress || null,
    meta: { orderingKey: 'ip_address' }
  },
  {
    header: 'Device',
    accessorKey: 'deviceKind',
    cell: ({ row }) => <NoddiBadge variant='outline'>{DEVICE_KIND_LABEL[row.original.deviceKind]}</NoddiBadge>,
    meta: {
      orderingKey: 'device_kind',
      description:
        'Desktop, mobile, tablet, or a bot, read from the user agent. Unknown when it was empty or not recognized.'
    }
  },
  {
    header: 'Device name',
    accessorKey: 'deviceName',
    cell: ({ row }) => row.original.deviceName || null,
    meta: {
      orderingKey: 'device_name',
      description:
        'Phone, tablet, or computer name. An iPhone stays iPhone, because the model is not in the user agent.'
    }
  },
  {
    header: 'Browser',
    accessorKey: 'browserName',
    cell: ({ row }) => row.original.browserName || null,
    meta: {
      orderingKey: 'browser_name',
      description:
        'Browser read from the user agent. Empty when none was recognized. The raw agent can still be copied.'
    }
  }
];

export const loginAttemptColumns: LoginAttemptColumn[] = [whenColumn, memberColumn, ...attemptColumns];

/** Drops the member column, for a list where every row is the same person. */
export const singleAccountLoginAttemptColumns: LoginAttemptColumn[] = [whenColumn, ...attemptColumns];

export const loginAttemptActions: TableAction<LoginAttemptRecord>[] = [
  {
    label: 'Copy email',
    actionType: 'copy',
    disabled: (row) => !row.email,
    onClick: (row) => void copyToClipboardWithToast(row.email, 'Email')
  },
  {
    label: 'Copy phone number',
    actionType: 'copy',
    disabled: (row) => !row.phoneNumber,
    onClick: (row) => void copyToClipboardWithToast(row.phoneNumber, 'Phone number')
  },
  {
    label: 'Copy IP address',
    actionType: 'copy',
    disabled: (row) => !row.ipAddress,
    onClick: (row) => void copyToClipboardWithToast(row.ipAddress ?? '', 'IP address')
  },
  {
    label: 'Copy user agent',
    actionType: 'copy',
    disabled: (row) => !row.userAgent,
    onClick: (row) => void copyToClipboardWithToast(row.userAgent, 'User agent')
  }
];
